import { hasLocalPermission, isLocalFlag, type LocalFlag } from "@bcmi-labs/cloud-sidebar"
import { useQuery } from "@tanstack/react-query"
import { useCallback } from "react"
import type { Arrayable } from "type-fest"

import { useSpace } from "@/api/hooks/useSpace"
import { policiesQuery } from "@/api/query/policies"
import { MY_SPACE } from "@/constants/spaces"
import type { Space } from "@/types"
import type { RBACFlag, RBACMatrix } from "@/types/rbac"

/**
 * Checks one or more local and remote permission flags against their respective sources.
 * Standalone (non-hook) so contexts outside a component render, e.g. the `routeGuard` route
 * loader, can run the same check `useRole`'s `can()` does.
 */
export function hasPermission(flag: {
  matrix: RBACMatrix
  role: Space["roles"][number] | undefined
  permission: Arrayable<RBACFlag | LocalFlag>
}) {
  const permissions = Array.isArray(flag.permission) ? flag.permission : [flag.permission]

  return permissions.every(permission =>
    isLocalFlag(permission) ? hasLocalPermission(flag.role, permission) : flag.matrix.policies[permission] === true
  )
}

/**
 * Centralized RBAC hook. Resolves the current user's role within the selected space and
 * exposes `can(flag)`, checking against the permissions API response.
 *
 * There is no "manage this asset kind" shortcut -- every call site names the individual
 * flag it needs (e.g. `"iot:thing::create"`) and checks it directly.
 */
export function useRole() {
  const { space } = useSpace()

  // The private "My Cloud" space carries a real (sentinel) ID, so identity is the only check.
  const isPrivateSpace = space.id === MY_SPACE.id

  const { data: matrix } = useQuery({
    // Disabled on the private space, where there is no organization header
    ...policiesQuery(space.id),
    enabled: !isPrivateSpace
  })

  // Real spaces always carry exactly one role; the private "My Cloud" space has none.
  const role = space.roles.length ? space.roles[0] : undefined

  const can = useCallback(
    (flag: RBACFlag | LocalFlag) => {
      if (isPrivateSpace) return true

      return hasPermission({ matrix: matrix || { policies: {} }, role, permission: flag })
    },
    [isPrivateSpace, matrix, role]
  )

  return {
    can
  }
}
